import { type FormEvent, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../lib/auth-context.js'
import { loadEphemeralSession } from '../lib/ephemeral-session.js'
import { SIGNUP_ERROR_FALLBACK, toUserMessage } from '../lib/error-copy.js'

export function SignupPage() {
  const { signUpWithEmail, signInWithGoogle } = useAuth()
  const navigate = useNavigate()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const validate = (): string | null => {
    if (password.length < 8) return 'パスワードは 8 文字以上にしてください'
    if (!/[A-Za-z]/.test(password) || !/[0-9]/.test(password)) {
      return 'パスワードは英数字を混在させてください'
    }
    if (password !== confirmPassword) return 'パスワードが一致しません'
    return null
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    const validationError = validate()
    if (validationError) {
      setError(validationError)
      return
    }
    setSubmitting(true)
    const wasEphemeral = !!loadEphemeralSession()
    try {
      await signUpWithEmail(email, password)
      navigate(wasEphemeral ? '/onboarding/migrated' : '/', { replace: true })
    } catch (err) {
      setError(toUserMessage(err, SIGNUP_ERROR_FALLBACK))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold">アカウント作成</h1>
          <p className="text-sm text-muted-foreground mt-1">テラファイル便を始めましょう</p>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium mb-1">
              メールアドレス
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-md bg-background"
            />
          </div>
          <div>
            <label htmlFor="password" className="block text-sm font-medium mb-1">
              パスワード（8文字以上、英数字混在）
            </label>
            <input
              id="password"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-md bg-background"
            />
          </div>
          <div>
            <label htmlFor="confirm-password" className="block text-sm font-medium mb-1">
              パスワード（確認）
            </label>
            <input
              id="confirm-password"
              type="password"
              autoComplete="new-password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-md bg-background"
            />
          </div>
          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-2">
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={submitting}
            className="w-full py-2 px-4 bg-foreground text-background rounded-md font-medium disabled:opacity-50"
          >
            {submitting ? '作成中…' : 'アカウント作成'}
          </button>
        </form>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-border" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">または</span>
          </div>
        </div>

        <button
          type="button"
          onClick={() => signInWithGoogle()}
          className="w-full py-2 px-4 border border-border rounded-md font-medium"
        >
          Google でアカウント登録
        </button>

        <p className="text-sm text-center text-muted-foreground">
          既にアカウントをお持ちですか？{' '}
          <Link to="/login" className="underline font-medium">
            ログイン
          </Link>
        </p>
      </div>
    </div>
  )
}
